/**
 * All site copy and data lives here so it can be edited in one place.
 * Only include claims that are true and demonstrable.
 */

export const site = {
  name: 'KBL Web Solutions',
  owner: 'Mirwes Hakimi',
  url: 'https://www.kblwebsolutions.com',
  email: 'mirwes210@gmail.com',
  location: 'Bay Area, California',
  github: 'https://github.com/Mirwes-Hakimi',
  linkedin: 'https://www.linkedin.com/in/mirwes-hakimi-065b651b7',
};

// EmailJS public identifiers (safe to expose in the browser).
export const emailjs = {
  service: 'service_05tn09g',
  template: 'template_k7nr79j',
  publicKey: '6LKuop0YHiirMBE0J',
};

export const navLinks = [
  { label: 'Services', id: 'services' },
  { label: 'Work', id: 'work' },
  { label: 'AI', id: 'ai' },
  { label: 'Process', id: 'process' },
  { label: 'About', id: 'about' },
];

export const technologies = [
  'React',
  'Next.js',
  'TypeScript',
  'Tailwind CSS',
  'Node.js',
  'Vite',
  'OpenAI API',
  'Vercel',
];

export const services = [
  {
    icon: 'Globe',
    title: 'Website Development',
    description:
      'Fast, mobile-first business websites, including multilingual sites, built with React and Next.js and set up to be found on search engines.',
    benefit: 'Look credible and get found by the customers you want.',
  },
  {
    icon: 'LayoutDashboard',
    title: 'Web Application Development',
    description:
      'Custom booking tools, portals, dashboards and internal apps designed around how your business actually runs.',
    benefit: 'Replace spreadsheets and manual steps with one clean tool.',
  },
  {
    icon: 'BrainCircuit',
    title: 'AI Integration',
    description:
      'Add chat, search, summaries or content tools to your website or app using the OpenAI API and your own business rules.',
    benefit: 'Answer questions faster and save your team time.',
  },
  {
    icon: 'Bot',
    title: 'AI Agents & Automation',
    description:
      'Assistants that take on repetitive work: sorting inquiries, drafting replies, updating records and triggering workflows.',
    benefit: 'Free your team for the work only people can do.',
  },
  {
    icon: 'RefreshCw',
    title: 'Website Redesign',
    description:
      'Modernize an outdated site with a cleaner design, faster loading and a better mobile experience, while keeping your brand.',
    benefit: 'Turn a dated site into one that earns trust.',
  },
  {
    icon: 'Plug',
    title: 'Business Technology Solutions',
    description:
      'Connect contact forms, email, payments and third-party tools so your website works with the rest of your business.',
    benefit: 'Fewer tools to juggle and a smoother customer journey.',
  },
];

const img = (name) => ({
  src720: `/images/projects/${name}-720.webp`,
  src1280: `/images/projects/${name}-1280.webp`,
});

export const projects = [
  {
    slug: 'dmv-permit-quiz',
    name: 'DMV Permit Quiz',
    category: 'Web app · Education',
    summary:
      'A free, bilingual practice test for the California DMV written exam, built for Farsi-speaking communities.',
    problem:
      'Farsi-speaking learners had few reliable study resources in their own language.',
    features: [
      '114 questions grounded in the official California Driver’s Handbook',
      '14 road-sign recognition questions',
      'English and Farsi, with instant scoring and no signup',
      'Local progress tracking so questions do not repeat',
    ],
    tech: ['Next.js', 'TypeScript', 'Tailwind CSS', 'Vercel'],
    image: img('dmv-permit-quiz'),
    alt: 'Homepage of the DMV Permit Quiz, a bilingual English and Farsi California DMV practice test built with Next.js',
    live: 'https://www.dmvfarsipashto.com',
  },
  {
    slug: 'linkneat',
    name: 'Linkneat Career & Tax Services',
    category: 'Business website · Multilingual',
    summary:
      'A professional website for a Sacramento tax, immigration, business and translation services firm.',
    problem:
      'The firm needed a trustworthy online presence for clients who prefer to be served in their own language.',
    features: [
      'Service overview for individuals and businesses',
      'Dari language panel alongside English content',
      'Clear “Book Free Consultation” calls to action',
    ],
    tech: ['React', 'Vite', 'Vercel'],
    image: img('linkneat'),
    alt: 'Homepage of Linkneat Career & Tax Services, a Sacramento tax and immigration services website with English and Dari content',
    live: 'https://www.linkneat.com',
  },
  {
    slug: 'dmv-test-car-rentals',
    name: 'DMV Test Car Rentals',
    category: 'Business website · Booking',
    summary:
      'A website for a Bay Area service that rents fully insured cars for DMV driving tests.',
    problem:
      'Test-takers without a suitable, insured vehicle need to understand the offer and book quickly.',
    features: [
      'Simple “How it works” walkthrough',
      'Transparent pricing section',
      'Booking calls to action in the header and hero',
    ],
    tech: ['React', 'React Router', 'Vite', 'Vercel'],
    image: img('dmv-test-car-rentals'),
    alt: 'Homepage of DMV Test Car Rentals, a San Francisco Bay Area DMV test car rental website',
    live: 'https://www.dmvtestcars.com',
  },
  {
    slug: 'northcal-furniture-gallery',
    name: 'NorthCal Furniture Gallery',
    category: 'Showroom website',
    summary:
      'An elegant website for a Sacramento furniture showroom, with collections, products and showroom visits.',
    problem:
      'Premium furniture needs an online presence that feels as refined as the showroom itself.',
    features: [
      'Collections and products sections',
      'Full-screen photography with a refined type system',
      '“Book a showroom visit” call to action',
    ],
    tech: ['React', 'React Router', 'Tailwind CSS', 'Vite', 'Vercel'],
    image: img('northcal-furniture-gallery'),
    alt: 'Homepage of NorthCal Furniture Gallery, a luxury furniture showroom website for Sacramento, California',
    live: 'https://north-cal-furniture-gallery.vercel.app',
  },
  {
    slug: 'danville-driving-school',
    name: 'Danville Driving School',
    category: 'Business website · Booking',
    summary:
      'A website for a Danville, California driving school covering programs, pricing, vehicles, reviews and FAQs.',
    problem:
      'Families need to compare programs, see the vehicles and get started quickly.',
    features: [
      'Programs and pricing overview',
      'Fleet showcase with vehicle details',
      'Reviews and FAQ sections',
      'Contact form with email delivery and a directions link',
    ],
    tech: ['React', 'React Router', 'Tailwind CSS', 'EmailJS', 'Vercel'],
    image: img('danville-driving-school'),
    alt: 'Homepage of Danville Driving School, a driving education website for Danville, California',
    live: 'https://danville-driving-school.vercel.app',
  },
];

export const aiCapabilities = [
  {
    icon: 'MessageSquare',
    title: 'AI chat experiences',
    description: 'Conversational assistants inside your website or app that guide visitors to what they need.',
  },
  {
    icon: 'Headset',
    title: 'AI-powered customer support',
    description: 'Answer common questions instantly from your own content, and hand off to you when it matters.',
  },
  {
    icon: 'PenLine',
    title: 'AI content tools',
    description: 'Drafting, summarizing and rewriting tools that speed up the writing your business already does.',
  },
  {
    icon: 'Workflow',
    title: 'AI automation',
    description: 'Automate repetitive steps such as sorting inquiries, tagging requests and preparing follow-ups.',
  },
  {
    icon: 'Bot',
    title: 'AI agents',
    description: 'Assistants that can take defined actions in your systems, within limits you control.',
  },
  {
    icon: 'Plug',
    title: 'OpenAI API integrations',
    description: 'Secure server-side integrations so API keys stay private and usage stays predictable.',
  },
  {
    icon: 'Blocks',
    title: 'Custom AI workflows',
    description: 'Multi-step flows tailored to your process, combining AI with your data, rules and approvals.',
  },
];

export const aiFlow = [
  {
    title: 'Your website or app',
    text: 'Where your customers already are.',
  },
  {
    title: 'Your secure backend',
    text: 'Applies your business rules and keeps API keys private.',
  },
  {
    title: 'AI model',
    text: 'Generates the answer or proposes the action.',
  },
  {
    title: 'Back to your customer',
    text: 'A helpful response, or an action in your systems.',
  },
];

export const processSteps = [
  {
    number: '01',
    icon: 'Compass',
    title: 'Discover',
    description: 'Understand the business, goals, customers, and requirements.',
  },
  {
    number: '02',
    icon: 'Palette',
    title: 'Design',
    description: 'Create the user experience and technical direction.',
  },
  {
    number: '03',
    icon: 'CodeXml',
    title: 'Build',
    description: 'Develop and integrate the website, application, APIs, and AI features.',
  },
  {
    number: '04',
    icon: 'Rocket',
    title: 'Launch & Improve',
    description: 'Deploy, test, measure, and continue improving the product.',
  },
];

export const principles = [
  {
    title: 'Plain-language and jargon-free',
    text: 'You will always know what is being built, why, and what it costs.',
  },
  {
    title: 'Fast, accessible, maintainable',
    text: 'Clean code, responsive layouts and sensible defaults for performance and accessibility.',
  },
  {
    title: 'Multilingual by experience',
    text: 'I have built for English, Farsi/Dari and Pashto audiences, and design for real people, not only browsers.',
  },
];

export const projectTypes = [
  'New website',
  'Website redesign',
  'Custom web application',
  'AI integration or agent',
  'Not sure yet',
];
